import {useOrderConfirmationScenario} from '@hconnect/tuc-processes/orderManagement/orderChange/orderConfirmation/hooks/useOrderConfirmationScenario'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {QuantityStep} from '../../../../shared/order-editor/components/fields/Quantity/QuantityStep'

/**
 * OrderManagement-specific wrapper for QuantityStep (before order started).
 * Uses order confirmation scenario hook to determine UI state.
 */
export const OrderManagementBeforeStartedQuantityStep: React.FC = () => {
  const {t} = useTranslation()
  const scenario = useOrderConfirmationScenario()

  // Format quantity string for display
  const getQuantityString = (value: number, uom: string) => {
    return `${value} ${uom.toUpperCase()}`
  }

  const quantityProps = useMemo(() => {
    // Scenario A: Default - no order confirmation logic
    if (scenario.type === 'default') {
      return {} as const
    }

    // Scenario B: Order confirmation with dispatch confirmation option
    if (scenario.allowsPendingDispatchApproval) {
      return {
        bannerText: scenario.requireDispatchConfirmation
          ? t(
              'orderIntake.rmc.orderManagement.quantityChange.orderConfirmationFlow.approvalWarning',
              'Quantity changes close to the delivery date need to be approved by dispatch.'
            )
          : undefined,
        hintText: t(
          'orderIntake.rmc.orderManagement.quantityChange.orderConfirmationFlow.manualApproveQuantityHint',
          'Any change below {{min}} or above {{max}} needs to be approved by dispatch',
          {
            min: getQuantityString(
              scenario.autoConfirmableRange.min,
              scenario.autoConfirmableRange.uom
            ),
            max: getQuantityString(
              scenario.autoConfirmableRange.max,
              scenario.autoConfirmableRange.uom
            )
          }
        ),
        state: scenario.requireDispatchConfirmation ? ('warning' as const) : undefined
      } as const
    }

    // Scenario C: Order confirmation without manual approval (restricted range)
    return {
      bannerText: undefined,
      hintText: t(
        'orderIntake.rmc.orderManagement.quantityChange.orderConfirmationFlow.restrictedRangeHint',
        'You can only request {{min}} to {{max}}',
        {
          min: getQuantityString(scenario.range.min, scenario.range.uom),
          max: getQuantityString(scenario.range.max, scenario.range.uom)
        }
      ),
      state: undefined
    } as const
  }, [scenario, t])

  return (
    <QuantityStep
      range={scenario.range}
      bannerText={quantityProps.bannerText}
      hintText={quantityProps.hintText}
      state={quantityProps.state}
      enableTruckSelector={false}
    />
  )
}
